import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Upload, Video } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import {
  sessionVideosQueryKey,
  useSessionVideos,
} from "@/hooks/useSessionVideos";
import { usePoll } from "@/hooks/usePoll";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import VideoUploadModal, { UploadStatus } from "./VideoUploadModal";
import VideoDetailsModal from "./VideoDetailsModal";
import VideoPlayerModal from "@/components/video/VideoPlayerModal";
import FileCard from "./FileCard";
import { FilesListLoading, FilesListError } from "./FilesListStates";
import { UploadStatusIcon } from "./uploadStatus";
import {
  getUploadStatusText,
  type UploadStatusLabels,
} from "./uploadStatusText";
import type {
  Session,
  SessionRecordingSummary,
  SessionVideosResponse,
  VideoFile,
  VideoProcessingStatus,
} from "@/types";
import { handleApiError } from "@/utils/errorHandler";
import type { ApiError } from "@/utils/errorHandler";
import { useAuth } from "@/hooks/useAuth";
import { useFailedImages } from "@/hooks/useFailedImages";
import {
  formatFileSize,
  formatUploadSpeed,
} from "@/utils/fileUtils";
import { formatUploadDuration } from "@/utils/duration";
import { openPresignedDownload } from "@/utils/download";
import { formatDateTimeOptional } from "@/utils/dateFormat";
import { groupVideoFilesByRecording } from "./recordingVideoFileGroups";
import RecordingVideoFileGroupHeader from "./RecordingVideoFileGroupHeader";
import { useTranslation } from "react-i18next";

interface VideoFilesListProps {
  session: Session;
  recordings?: SessionRecordingSummary[];
  onVideoDeleted?: () => void;
  onVideoUploaded?: () => void;
  onRefresh?: (refreshFn: () => void) => void;
  onVideosChange?: (videos: VideoFile[]) => void;
  onUploadStateChange?: (state: VideoUploadState) => void;
  showUploadButton?: boolean;
  isUploadModalOpen?: boolean;
  onUploadModalOpenChange?: (isOpen: boolean) => void;
  isContentVisible?: boolean;
  contentId?: string;
  contentClassName?: string;
}

const PROCESSING_STATUS_FETCH_DELAY_MS = 1000;
const PROCESSING_STATUS_POLL_INTERVAL_MS = 3000;

export interface VideoUploadState {
  status: UploadStatus;
  progress: number;
  filename: string | null;
}

export default function VideoFilesList({
  session,
  recordings,
  onVideoDeleted,
  onVideoUploaded,
  onRefresh,
  onVideosChange,
  onUploadStateChange,
  showUploadButton = true,
  isUploadModalOpen: controlledIsUploadModalOpen,
  onUploadModalOpenChange,
  isContentVisible = true,
  contentId,
  contentClassName,
}: VideoFilesListProps) {
  const { user } = useAuth();
  const { t } = useTranslation(["pages", "common"]);
  const isSessionOwner = user?.id === session.userId;
  const queryClient = useQueryClient();
  const {
    videos,
    isLoading,
    error: videosError,
    refetch,
  } = useSessionVideos(session.id);
  const error = videosError ? t("pages:sessions.videoList.loadError") : "";

  useEffect(() => {
    if (videosError) {
      console.error("Error fetching videos:", videosError);
    }
  }, [videosError]);
  const [processingStatuses, setProcessingStatuses] = useState<
    Record<string, VideoProcessingStatus>
  >({});
  const [videoToDelete, setVideoToDelete] = useState<VideoFile | null>(null);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  const { hasFailed: hasFailedThumbnail, markFailed: markThumbnailFailed } =
    useFailedImages();
  const [internalIsUploadModalOpen, setInternalIsUploadModalOpen] =
    useState(false);
  const [selectedVideoForDetails, setSelectedVideoForDetails] =
    useState<VideoFile | null>(null);
  const [selectedVideoForPlayback, setSelectedVideoForPlayback] =
    useState<VideoFile | null>(null);

  const groupedVideos = useMemo(
    () => groupVideoFilesByRecording(videos, recordings),
    [recordings, videos],
  );

  // Upload state - persists across modal open/close
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const isUploadModalOpen =
    controlledIsUploadModalOpen ?? internalIsUploadModalOpen;
  const setIsUploadModalOpen = useCallback(
    (isOpen: boolean) => {
      if (controlledIsUploadModalOpen === undefined) {
        setInternalIsUploadModalOpen(isOpen);
      }
      onUploadModalOpenChange?.(isOpen);
    },
    [controlledIsUploadModalOpen, onUploadModalOpenChange]
  );

  useEffect(() => {
    onUploadStateChange?.({
      status: uploadStatus,
      progress: uploadProgress,
      filename: selectedFile?.name ?? null,
    });
  }, [onUploadStateChange, selectedFile, uploadProgress, uploadStatus]);

  const fetchProcessingStatus = useCallback(async (videoId: string) => {
    try {
      const status = await apiClient.getVideoProcessingStatus(videoId);
      setProcessingStatuses((prev) => ({
        ...prev,
        [videoId]: status,
      }));
    } catch (err) {
      // Don't show error for processing status, just log it
      console.error(`Error fetching processing status for ${videoId}:`, err);
    }
  }, []);

  // Status fetches are delayed so the backend has a moment to create
  // processing jobs; timers are tracked so they are cleared on unmount.
  const pendingStatusFetchTimeoutsRef = useRef<
    Set<ReturnType<typeof setTimeout>>
  >(new Set());

  const scheduleProcessingStatusFetch = useCallback(
    (videoId: string) => {
      const timeoutId = setTimeout(() => {
        pendingStatusFetchTimeoutsRef.current.delete(timeoutId);
        fetchProcessingStatus(videoId);
      }, PROCESSING_STATUS_FETCH_DELAY_MS);
      pendingStatusFetchTimeoutsRef.current.add(timeoutId);
    },
    [fetchProcessingStatus],
  );

  useEffect(() => {
    const pendingTimeouts = pendingStatusFetchTimeoutsRef.current;
    return () => {
      pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      pendingTimeouts.clear();
    };
  }, []);

  useEffect(() => {
    videos.forEach((video) => {
      if (video.uploadStatus === "completed") {
        scheduleProcessingStatusFetch(video.id);
      }
    });
  }, [videos, scheduleProcessingStatusFetch]);

  useEffect(() => {
    onVideosChange?.(videos);
  }, [onVideosChange, videos]);

  // Expose refresh function via callback
  useEffect(() => {
    if (onRefresh) {
      onRefresh(refetch);
    }
  }, [onRefresh, refetch]);

  // Polling for processing status updates
  const completedVideoIds = useMemo(
    () =>
      videos
        .filter((video) => video.uploadStatus === "completed")
        .map((video) => video.id)
        .join(","),
    [videos]
  );

  const shouldPollProcessingStatuses = useMemo(() => {
    const ids = completedVideoIds.split(",").filter(Boolean);
    const hasActiveProcessing = Object.values(processingStatuses).some(
      (status) =>
        status.processingJobs.some(
          (job) => job.status === "processing" || job.status === "pending"
        )
    );
    const hasCompletedVideosWithoutStatus = ids.some(
      (id) => !processingStatuses[id]
    );

    return (
      ids.length > 0 && (hasActiveProcessing || hasCompletedVideosWithoutStatus)
    );
  }, [processingStatuses, completedVideoIds]);

  // Poll every 3 seconds while there are active processing jobs or videos
  // without status
  usePoll(
    () => {
      completedVideoIds
        .split(",")
        .filter(Boolean)
        .forEach((id) => {
          fetchProcessingStatus(id);
        });
    },
    {
      intervalMs: PROCESSING_STATUS_POLL_INTERVAL_MS,
      enabled: shouldPollProcessingStatuses,
    },
  );

  const formatUploadDate = (dateString: string | null) =>
    formatDateTimeOptional(
      dateString,
      t("pages:sessions.videoList.unknownDate"),
    );

  const handleDownload = async (video: VideoFile) => {
    try {
      // Get presigned URL for secure download
      const downloadData = await apiClient.getVideoDownloadUrl(video.id);

      // The server's Content-Disposition header names the file.
      openPresignedDownload(downloadData.downloadUrl);
    } catch (error) {
      console.error("Error downloading video:", error);
      // Fallback to direct URL if presigned URL fails
      openPresignedDownload(video.url);
    }
  };

  const getProcessingBadgeColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "processing":
        return "bg-primary-100 text-primary-700";
      case "failed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-secondary-100 text-secondary-600";
    }
  };

  const getProcessingIcon = (status: string) => {
    switch (status) {
      case "completed":
        return "✓";
      case "processing":
        return "⏳";
      case "failed":
        return "✗";
      default:
        return "⏸";
    }
  };

  const renderProcessingBadges = (videoId: string) => {
    const status = processingStatuses[videoId];

    // Show "Processing..." if we don't have status yet for a completed upload
    if (!status) {
      return (
        <>
          <span className="sm:inline hidden">•</span>
          <div className="flex items-center gap-1">
            <span className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded animate-pulse">
              {t("pages:sessions.videoList.badgeProcessing")}
            </span>
          </div>
        </>
      );
    }

    if (status.jobCount === 0) {
      return (
        <>
          <span className="sm:inline hidden">•</span>
          <div className="flex items-center gap-1">
            <span className="px-2 py-1 text-xs bg-secondary-100 text-secondary-600 rounded">
              {t("pages:sessions.videoList.badgeNoJobs")}
            </span>
          </div>
        </>
      );
    }

    return (
      <>
        <span className="sm:inline hidden">•</span>
        <div className="flex items-center gap-1 flex-wrap">
          {status.processingJobs.map((job) => (
            <span
              key={job.id}
              className={`px-2 py-1 text-xs rounded ${getProcessingBadgeColor(job.status)} ${
                job.status === "processing" ? "animate-pulse" : ""
              }`}
              title={job.message || `${job.resolutionLabel} ${t(`pages:sessions.jobStatus.${job.status}`, { defaultValue: job.status })}`}
            >
              {job.resolutionLabel} {getProcessingIcon(job.status)}
            </span>
          ))}
        </div>
      </>
    );
  };

  const handleDeleteClick = (video: VideoFile) => {
    setVideoToDelete(video);
  };

  const handleConfirmDelete = async () => {
    if (!videoToDelete) return;

    try {
      setIsDeletingVideo(true);
      await apiClient.deleteVideo(videoToDelete.id);

      // Remove the video from the session-videos query cache so the list and
      // parent consumers (onVideosChange) update immediately.
      queryClient.setQueryData<SessionVideosResponse>(
        sessionVideosQueryKey(session.id),
        (existing) =>
          existing
            ? {
                ...existing,
                videos: existing.videos.filter(
                  (video) => video.id !== videoToDelete.id,
                ),
              }
            : existing,
      );
      setVideoToDelete(null);

      // Notify parent component
      onVideoDeleted?.();
    } catch (error) {
      console.error("Error deleting video:", error);
      handleApiError(error as ApiError, t("pages:sessions.videoList.deleteError"));
    } finally {
      setIsDeletingVideo(false);
    }
  };

  const handleCancelDelete = () => {
    setVideoToDelete(null);
  };

  const handleUploadComplete = () => {
    refetch();
    onVideoUploaded?.();

    // Reset upload state after completion
    setSelectedFile(null);
    setUploadStatus("idle");
    setUploadProgress(0);
    setUploadSpeed(0);
    setEstimatedTimeRemaining(0);
    setUploadError("");
  };

  const renderUploadButton = () => {
    if (!isSessionOwner || !showUploadButton) return null;

    return (
      <button
        onClick={() => setIsUploadModalOpen(true)}
        className={`px-3 py-1.5 text-sm flex items-center space-x-2 ${
          uploadStatus === "uploading" ? "btn-secondary" : "btn-primary"
        }`}
        title={
          uploadStatus === "uploading"
            ? t("pages:sessions.videoList.titleUploading", { progress: uploadProgress })
            : t("pages:sessions.videoList.titleUpload")
        }
      >
        <Upload className="h-4 w-4" />
        <span>
          {uploadStatus === "uploading"
            ? t("pages:sessions.videoList.buttonUploading", { progress: uploadProgress })
            : t("pages:sessions.videoList.buttonUpload")}
        </span>
      </button>
    );
  };

  const renderUploadProgressBanner = () => {
    if (uploadStatus !== "uploading") return null;

    return (
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
        <div className="flex items-center justify-between text-sm text-primary-700 mb-2">
          <span>{t("pages:sessions.videoList.uploadingFilename", { name: selectedFile?.name })}</span>
          <span>{uploadProgress}%</span>
        </div>
        <div className="w-full bg-primary-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
        {uploadSpeed > 0 && (
          <div className="flex items-center justify-between text-xs text-primary-600 mt-1">
            <span>{t("pages:sessions.upload.common.speed")} {formatUploadSpeed(uploadSpeed)}</span>
            {estimatedTimeRemaining > 0 && (
              <span>{t("pages:sessions.videoList.eta")} {formatUploadDuration(estimatedTimeRemaining)}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <FilesListLoading label={t("pages:sessions.videoList.loading")} />
    );
  }

  if (error) {
    return <FilesListError message={error} />;
  }

  const statusLabels: UploadStatusLabels = {
    uploaded: t("pages:sessions.videoList.statusUploaded"),
    uploading: t("pages:sessions.videoList.statusUploading"),
    failed: t("pages:sessions.videoList.statusFailed"),
    pending: t("pages:sessions.videoList.statusPending"),
  };

  const renderVideoCard = (video: VideoFile, recordingGroup: ReturnType<typeof groupVideoFilesByRecording>[number]["recordingGroup"], startsRecordingGroup: boolean) => {
    const isCompleted = video.uploadStatus === "completed";
    const showDateRow = !!video.uploadedAt || isCompleted;
    // Leaving media undefined lets FileCard fall back to its icon, which is
    // also what a thumbnail whose presigned URL has expired must do.
    const thumbnailUrl =
      video.thumbnailUrl && !hasFailedThumbnail(video.thumbnailUrl)
        ? video.thumbnailUrl
        : null;

    return (
      <FileCard
        key={video.id}
        icon={Video}
        media={
          thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              loading="lazy"
              onError={() => markThumbnailFailed(thumbnailUrl)}
              className="h-9 w-14 rounded object-cover flex-shrink-0 bg-secondary-100"
            />
          ) : undefined
        }
        filename={video.filename}
        header={
          startsRecordingGroup ? (
            <RecordingVideoFileGroupHeader group={recordingGroup} />
          ) : undefined
        }
        statusIcon={<UploadStatusIcon status={video.uploadStatus} />}
        statusText={getUploadStatusText(video.uploadStatus, statusLabels)}
        fileSizeLabel={formatFileSize(video.fileSize)}
        showSizeSeparator
        onDetails={() => setSelectedVideoForDetails(video)}
        onPlay={
          isCompleted ? () => setSelectedVideoForPlayback(video) : undefined
        }
        onDownload={
          isSessionOwner && isCompleted ? () => handleDownload(video) : undefined
        }
        onDelete={isSessionOwner ? () => handleDeleteClick(video) : undefined}
        labels={{
          details: t("pages:sessions.videoList.titleViewDetails"),
          play: t("pages:sessions.videoList.titleWatch"),
          download: t("pages:sessions.videoList.titleDownload"),
          delete: t("pages:sessions.videoList.titleDelete"),
        }}
        mobileDetails={
          showDateRow ? (
            <>
              {video.uploadedAt && (
                <div className="flex items-center gap-1.5">
                  <span>{formatUploadDate(video.uploadedAt)}</span>
                </div>
              )}
              {isCompleted && (
                <div className="flex items-center gap-2">
                  {renderProcessingBadges(video.id)}
                </div>
              )}
            </>
          ) : undefined
        }
        desktopMeta={
          <>
            {video.uploadedAt && (
              <>
                <span className="sm:inline hidden">•</span>
                <span>{formatUploadDate(video.uploadedAt)}</span>
              </>
            )}
            {isCompleted && renderProcessingBadges(video.id)}
          </>
        }
      />
    );
  };

  const renderContent = () => {
    if (videos.length === 0) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-secondary-500">{t("pages:sessions.videoList.zeroVideos")}</span>
            {renderUploadButton()}
          </div>

          {renderUploadProgressBanner()}

          <div className="flex flex-col items-center justify-center py-12 text-secondary-500">
            <Video className="w-12 h-12 mb-2" />
            <p>{t("pages:sessions.videoList.noVideos")}</p>
            {isSessionOwner && (
              <p className="text-sm">{t("pages:sessions.videoList.noVideosHint")}</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-secondary-500">
            {t("pages:sessions.videoList.videoCount", { count: videos.length })}
          </span>
          {renderUploadButton()}
        </div>

        {renderUploadProgressBanner()}

        <div className="space-y-3">
          {groupedVideos.map(({ video, recordingGroup, startsRecordingGroup }) =>
            renderVideoCard(video, recordingGroup, startsRecordingGroup),
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        id={contentId}
        hidden={!isContentVisible}
        className={contentClassName}
      >
        {renderContent()}
      </div>

      {/* Upload Modal */}
      {session && isSessionOwner && (
        <VideoUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          session={session}
          onUploadComplete={handleUploadComplete}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          uploadStatus={uploadStatus}
          setUploadStatus={setUploadStatus}
          uploadProgress={uploadProgress}
          setUploadProgress={setUploadProgress}
          uploadSpeed={uploadSpeed}
          setUploadSpeed={setUploadSpeed}
          estimatedTimeRemaining={estimatedTimeRemaining}
          setEstimatedTimeRemaining={setEstimatedTimeRemaining}
          error={uploadError}
          setError={setUploadError}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!videoToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("pages:sessions.videoList.deleteTitle")}
        message={
          videoToDelete
            ? t("pages:sessions.videoList.deleteMessage", { name: videoToDelete.filename })
            : ""
        }
        confirmButtonText={t("common:delete")}
        confirmButtonColor="red"
        isLoading={isDeletingVideo}
      />

      {/* Video Details Modal */}
      <VideoDetailsModal
        isOpen={!!selectedVideoForDetails}
        onClose={() => setSelectedVideoForDetails(null)}
        video={selectedVideoForDetails}
        processingStatus={
          selectedVideoForDetails
            ? processingStatuses[selectedVideoForDetails.id]
            : undefined
        }
        processingJobs={
          selectedVideoForDetails
            ? (processingStatuses[selectedVideoForDetails.id]?.processingJobs ??
              [])
            : []
        }
      />

      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={!!selectedVideoForPlayback}
        onClose={() => setSelectedVideoForPlayback(null)}
        playlist={selectedVideoForPlayback ? [selectedVideoForPlayback] : []}
        canDownload={isSessionOwner}
      />
    </>
  );
}
